import { useState, useMemo, useCallback, useEffect } from "react";
import { MyContractsTable } from "../components/organisms/MyContractsTable";
import { ActivityStreamDrawer } from "../components/organisms/ActivityStreamDrawer";
import { useMyContracts } from "../hooks/useMyContracts";
import { DEFAULT_LIST_PAGE_SIZE } from "../constants/listPagination";
import { useBusinessUnits } from "../hooks/useBusinessUnits";
import { ALL_BUSINESS_UNITS_SENTINEL } from "../utils/businessUnitApiCodes";
import { useContractCategories } from "../hooks/useContractCategories";
import {
  formatGeneratedContractStatusLabel,
  GENERATED_CONTRACT_DB_STATUSES
} from "../constants/generatedContractStatuses";

export const MyContractsPage = () => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_LIST_PAGE_SIZE);
  const [activityContractId, setActivityContractId] = useState<string | null>(
    null
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [filterType, setFilterType] = useState("");
  const [filterOrigination, setFilterOrigination] = useState("");
  /** Empty string = All (no BU filter). Otherwise BU `code` from GET /business-units. */
  const [filterBusinessUnit, setFilterBusinessUnit] = useState("");
  /** DB `status` value, or empty = all */
  const [filterStatus, setFilterStatus] = useState("");
  const { options: businessUnitOptions } = useBusinessUnits();
  const { categories: contractCategories, loading: categoriesLoading } = useContractCategories();

  useEffect(() => {
    setPage(1);
  }, [filterType, filterOrigination, filterBusinessUnit, filterStatus]);

  const statusForApi = useMemo(
    () => (filterStatus.trim() ? [filterStatus.trim()] : undefined),
    [filterStatus]
  );

  const businessUnitForApi = useMemo(() => {
    if (!filterBusinessUnit.trim()) return undefined;
    const opt = businessUnitOptions.find((o) => o.code === filterBusinessUnit);
    const raw = (opt?.name ?? filterBusinessUnit).trim();
    return raw || undefined;
  }, [filterBusinessUnit, businessUnitOptions]);

  const searchForApi = searchTerm.trim() || undefined;

  const { contracts, total, loading, error, reload } = useMyContracts({
    page,
    perPage,
    contract_type: filterType.trim() || undefined,
    contract_origination: filterOrigination.trim() || undefined,
    business_unit: businessUnitForApi,
    q: searchForApi,
    status: statusForApi
  });

  const maxPageServer = useMemo(() => {
    if (total <= 0 || perPage <= 0) {
      return 1;
    }
    return Math.max(1, Math.ceil(total / perPage));
  }, [total, perPage]);

  useEffect(() => {
    if (page > maxPageServer) {
      setPage(maxPageServer);
    }
  }, [page, maxPageServer]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    []
  );

  const clearFilters = useCallback(() => {
    setFilterType("");
    setFilterOrigination("");
    setFilterBusinessUnit("");
    setFilterStatus("");
    setSearchTerm("");
  }, []);

  const businessUnitSelectRows = useMemo(
    () =>
      businessUnitOptions.filter((o) => o.code !== ALL_BUSINESS_UNITS_SENTINEL.code),
    [businessUnitOptions]
  );

  const hasActiveFilterDropdowns =
    filterType || filterOrigination || filterBusinessUnit || filterStatus;

  const handleChangePage = useCallback((p: number) => {
    setPage(p);
  }, []);

  const handleChangeRowsPerPage = useCallback((next: number) => {
    setPerPage(next);
    setPage(1);
  }, []);

  return (
    <section className="page-section">
      <ActivityStreamDrawer
        isOpen={activityContractId !== null}
        generatedContractId={activityContractId}
        onClose={() => setActivityContractId(null)}
      />

      <div className="page-header">
        <h1 className="text-2xl font-bold text-heading">Contracts</h1>
      </div>

      {error ? (
        <div
          className="my-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
          role="alert"
        >
          <p>{error}</p>
          <button
            type="button"
            onClick={() => reload()}
            className="mt-2 text-sm font-medium text-red-700 underline transition-all duration-200 ease-in-out hover:text-red-900"
          >
            Try again
          </button>
        </div>
      ) : null}

      <div className="page-toolbar flex flex-wrap items-center gap-3">
        <div className="search-wrapper max-w-sm min-w-[200px] flex-1">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="search-icon h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            placeholder="Search contracts..."
            value={searchTerm}
            onChange={handleSearchChange}
            disabled={loading}
            className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 transition-all duration-200 ease-in-out focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-50"
          />
        </div>

        <div className="relative">
          <button
            type="button"
            onClick={() => setShowFilter((prev) => !prev)}
            disabled={loading}
            className="filter-btn inline-flex items-center gap-2"
            aria-expanded={showFilter}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            Filter
            {hasActiveFilterDropdowns && (
              <span
                className="flex h-4 w-4 items-center justify-center rounded-full bg-primary-500 text-[10px] font-bold text-white"
                aria-hidden
              >
                !
              </span>
            )}
          </button>

          {showFilter && (
            <div className="filter-dropdown absolute left-0 top-full z-20 mt-2 w-72 rounded-xl border border-gray-200 bg-white p-4 shadow-lg">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-900">Filters</p>
                {hasActiveFilterDropdowns && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="text-xs font-medium text-primary-500 transition-all duration-200 ease-in-out hover:text-primary-600"
                  >
                    Clear all
                  </button>
                )}
              </div>
              <div className="space-y-3">
                <div>
                  <label
                    htmlFor="ft"
                    className="mb-1 block text-xs font-medium text-gray-600"
                  >
                    Contract Type
                  </label>
                  <select
                    id="ft"
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    disabled={categoriesLoading}
                    className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm disabled:cursor-not-allowed disabled:bg-gray-100"
                  >
                    <option value="">All</option>
                    {contractCategories.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="fo"
                    className="mb-1 block text-xs font-medium text-gray-600"
                  >
                    Origination
                  </label>
                  <select
                    id="fo"
                    value={filterOrigination}
                    onChange={(e) => setFilterOrigination(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
                  >
                    <option value="">All</option>
                    <option value="Internal">Internal</option>
                    <option value="External">External</option>
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="fb"
                    className="mb-1 block text-xs font-medium text-gray-600"
                  >
                    Business Unit
                  </label>
                  <select
                    id="fb"
                    value={filterBusinessUnit}
                    onChange={(e) => setFilterBusinessUnit(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
                  >
                    <option value="">All</option>
                    {businessUnitSelectRows.map((o) => (
                      <option key={o.code} value={o.code}>
                        {o.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="fs"
                    className="mb-1 block text-xs font-medium text-gray-600"
                  >
                    Status
                  </label>
                  <select
                    id="fs"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
                  >
                    <option value="">All</option>
                    {GENERATED_CONTRACT_DB_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {formatGeneratedContractStatusLabel(s)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <MyContractsTable
        contracts={contracts}
        onOpenActivityStream={(c) => setActivityContractId(c.id)}
        paginationServer
        totalRows={total}
        page={page}
        perPage={perPage}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        loading={loading}
      />
    </section>
  );
};
